import {useId} from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import {useTranslation} from 'react-i18next';

export interface NodeSelectOption {
    value: string;
    label: string;
}

interface NodeSelectFieldProps {
    label: string;
    value: string;
    options: NodeSelectOption[];
    onChange: (value: string) => void;
    /** The name stored beside the value, shown when the catalogue no longer offers it. */
    unknownValueLabel?: string;
    /** While true, nothing reads "(no longer available)". */
    loading?: boolean;
    disabled?: boolean;
}

/**
 * Legacy select (new code uses `common/newSelect`). A stored id the options lack stays selected as a disabled
 * option, never "Any …"; it reads "(no longer available)" only once `loading` is false and a real option exists.
 */
const NodeSelectField = ({
    label,
    value,
    options,
    onChange,
    unknownValueLabel,
    loading,
    disabled
}: NodeSelectFieldProps) => {
    const {t} = useTranslation();
    const labelId = useId();

    const isKnown = options.some(option => option.value === value);
    const showUnavailable = !!value && !isKnown;
    // The "Any …" entry is always there; it says nothing about whether the catalogue arrived.
    const settled = !loading && options.some(option => !!option.value);

    return (
        <FormControl fullWidth size={'small'} disabled={disabled}>
            <InputLabel id={labelId} shrink>{label}</InputLabel>
            <Select
                labelId={labelId}
                displayEmpty
                input={<OutlinedInput notched label={label}/>}
                value={isKnown || showUnavailable ? value : ''}
                onChange={event => onChange(event.target.value)}
            >
                {options.map(option => (
                    <MenuItem key={option.value || 'any'} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}

                {showUnavailable && (
                    <MenuItem key={value} value={value} disabled>
                        {settled
                            ? t('{{name}} (no longer available)', {name: unknownValueLabel || value})
                            : (unknownValueLabel || value)}
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    );
};

export default NodeSelectField;
